import {
    AlloyPageState,
    ReducerActionNames,
    AlloyPageErrors,
    AlloyProcessStages
} from './AlloyRepairRequestTypes';
import { AddressType, UploadedImage } from '../../common/CommonTypes';
import { UploadImageTypes } from '../../../Constants/Common';
import { SelectInputHelper } from '../../common/selectInput/SelectInput';

//Please add additional types with | 'your type'
type Action =
    | { type: 'set-alloy-type'; alloyType: string }
    | { type: 'update-alloy-errors'; alloyErrors: AlloyPageErrors }
    | { type: 'update-mileage'; mileage: string | number }
    | { type: 'update-uploaded-images'; uploadedImages: UploadedImage[] }
    | {
          type: 'update-wheel-repair-request';
          key: string;
          value: string;
      }
    | { type: 'start-new-wheel-request' }
    | { type: 'fetch-current-wheel-request' }
    | { type: 'update-alloy-stage'; newStage: AlloyProcessStages }
    | { type: 'add-wheel-request' }
    | { type: 'set-policy-data'; address: AddressType }
    | { type: 'set-repair-address-selected'; value: number }
    | { type: 'set-has-errors'; hasErrors: boolean }
    | { type: 'set-permission-granted'; permissionGranted: boolean }
    | {
          type: 'remove-selected-wheel';
          wheel: string;
          newStage: AlloyProcessStages;
      }
    | { type: 'set-wheel'; wheel: string; productReference: string }
    | { type: 'view-wheel'; wheel: string; newStage: AlloyProcessStages }
    | { type: 'update-wheel'; updateWheel: string }
    | { type: 'scroll-to-address'; scrollToAddress: boolean }
    | { type: 'show-alert'; showCancelAlert: boolean }
    | {
          type: 'set-loading-request-submission';
          loadingOnRequestSubmission: boolean;
      };

const AlloyRepairRequestReducer = (
    state: AlloyPageState,
    action: Action
): AlloyPageState => {
    switch (action.type) {
        case ReducerActionNames.SelectAlloyType:
            return {
                ...state,
                alloyType: action.alloyType
            };

        case ReducerActionNames.UpdateAlloyErrors:
            return {
                ...state,
                alloyFormErrors: action.alloyErrors
            };
        case ReducerActionNames.SetHasErrors:
            return {
                ...state,
                hasErrors: action.hasErrors
            };
        case ReducerActionNames.UpdateMileage:
            return {
                ...state,
                mileage: action.mileage
            };
        case ReducerActionNames.UpdateWheelRepairRequest: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                [action.key]: action.value
            };

            const updatedRepairRequests = state.alloyWheelRepairRequests.map(
                (repairRequest, index) => {
                    if (index !== state.repairRequestSelectedIndex) {
                        return repairRequest;
                    } else {
                        return updatedCurrentRepairRequest;
                    }
                }
            );

            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest,
                alloyWheelRepairRequests: updatedRepairRequests
            };
        }
        case ReducerActionNames.UpdateUploadedImages: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    uploadedImages: action.uploadedImages
                }
            };
        }
        case ReducerActionNames.UpdateAlloyStage: {
            return {
                ...state,
                alloyStage: action.newStage
            };
        }
        case ReducerActionNames.SetPolicyData: {
            return {
                ...state,
                repairAddresses: [...state.repairAddresses, action.address]
            };
        }
        case ReducerActionNames.UpdateRepairAddressSelected: {
            return {
                ...state,
                repairAddressSelectedIndex: action.value
            };
        }
        case ReducerActionNames.SetPermissionGranted: {
            return {
                ...state,
                permissionGranted: action.permissionGranted
            };
        }
        case ReducerActionNames.RemoveWheel: {
            const newSelectedWheels = state.selectedWheels.filter(
                (wheel) => wheel !== action.wheel
            );

            const newAlloyWheelRepairRequests =
                state.alloyWheelRepairRequests.filter(
                    (rr) => rr.damagedWheel !== action.wheel
                );

            const repairRequestIndex = newAlloyWheelRepairRequests.length - 1;

            return {
                ...state,
                selectedWheels: newSelectedWheels,
                alloyWheelRepairRequests: newAlloyWheelRepairRequests,
                repairRequestSelectedIndex: repairRequestIndex,
                currentRepairRequest:
                    newAlloyWheelRepairRequests[repairRequestIndex],
                alloyStage: action.newStage
            };
        }
        case ReducerActionNames.SetWheel: {
            const repairRequestIndex = state.alloyWheelRepairRequests.findIndex(
                (rr) => rr.damagedWheel === action.wheel
            );

            if (repairRequestIndex > -1) {
                if (repairRequestIndex === state.repairRequestSelectedIndex) {
                    return {
                        ...state,
                        alloyFormErrors: {}
                    };
                }

                return {
                    ...state,
                    alloyFormErrors: {},
                    repairRequestSelectedIndex: repairRequestIndex,
                    currentRepairRequest:
                        state.alloyWheelRepairRequests[repairRequestIndex]
                };
            } else {
                return createNewRepairRequest(
                    state,
                    action.wheel,
                    action.productReference
                );
            }
        }
        case ReducerActionNames.AddWheelRequest: {
            const newSelectedWheels = state.currentRepairRequest.damagedWheel;
            const repairRequestIndex = state.alloyWheelRepairRequests.findIndex(
                (rr) => rr.damagedWheel === newSelectedWheels
            );
            const newRepairRequest = state.currentRepairRequest;
            if (repairRequestIndex > -1) {
                return state;
            } else {
                return {
                    ...state,
                    selectedWheels: [
                        ...state.selectedWheels,
                        newSelectedWheels
                    ],
                    alloyWheelRepairRequests: [
                        ...state.alloyWheelRepairRequests,
                        newRepairRequest
                    ],
                    currentRepairRequest: newRepairRequest
                };
            }
        }
        case ReducerActionNames.ShowCancelAlert: {
            return {
                ...state,
                showCancelAlert: action.showCancelAlert
            };
        }
        case ReducerActionNames.UpdateWheel: {
            return {
                ...state,
                updateWheel: action.updateWheel
            };
        }
        case ReducerActionNames.ScrollToAddress: {
            return {
                ...state,
                scrollToAddress: action.scrollToAddress
            };
        }
        case ReducerActionNames.LoadingOnRequestSubmission: {
            return {
                ...state,
                isLoadingOnSubmission: action.loadingOnRequestSubmission
            };
        }
        default:
            return state;
    }
};

const createNewRepairRequest = (
    state: AlloyPageState,
    wheel: string,
    productReference: string
): AlloyPageState => {
    const newIndex = state.alloyWheelRepairRequests.length;

    const newRepairRequest = {
        id: newIndex,
        productReference: productReference,
        dateOfIncident: undefined,
        damagedWheel: wheel,
        damageType: SelectInputHelper.NullValue(),
        damageSize: SelectInputHelper.NullValue(),
        additionalInformation: '',
        uploadedImages: [
            {
                type: UploadImageTypes.CloseUp,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Distance,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage1,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage2,
                uploadedPath: ''
            }
        ]
    };

    return {
        ...state,
        alloyFormErrors: {},
        repairRequestSelectedIndex: newIndex,
        currentRepairRequest: newRepairRequest
    };
};

export default AlloyRepairRequestReducer;
